import axios from 'axios'
import { toastr } from 'react-redux-toastr'
import { initialize } from 'redux-form'

const INITIAL_STATE = {
    list: [],
    show: 'list',
    errors: {}
}

const INITIAL_VALUES = {
    title: '',
    min_result: 2,
    history_days: 7,
    system_prompt: '',
    instruction: '',
    schedules_text: '08:00,08:00,12:59,manhã\n13:00,13:00,17:59,tarde\n18:00,18:00,23:59,noite',
    active: 1,
}

function schedulesToText(schedules) {
    if (!schedules) return INITIAL_VALUES.schedules_text
    let rows = schedules
    if (typeof schedules === 'string') {
        try { rows = JSON.parse(schedules) } catch (e) { return schedules }
    }
    if (!Array.isArray(rows)) return INITIAL_VALUES.schedules_text
    return rows.map((r) => [r.at || r.start, r.start, r.end, r.label || ''].join(',')).join('\n')
}

function textToSchedules(text) {
    const slots = []
    String(text || '').split(/\r\n|\r|\n/).forEach((line) => {
        const parts = line.split(',').map((p) => p.trim()).filter((p, i, arr) => i < 4 || p)
        if (parts.length < 3) return
        slots.push({
            at: parts[0],
            start: parts[1],
            end: parts[2],
            label: parts[3] || `${parts[1]}-${parts[2]}`,
        })
    })
    return slots
}

export function getList() {
    return dispatch => {
        axios.get(`${process.env.REACT_APP_API_HOST}/crash_prompts?orderBy=id&sortedBy=asc&limit=100`)
            .then(resp => {
                dispatch({
                    type: 'CRASH_PROMPT_FETCHED',
                    payload: resp
                })
            })
            .catch(e => {
                const msg = (e.response && e.response.data && e.response.data.message)
                    || (e.response && e.response.status === 401 && 'Sem permissão para listar prompts. Saia e entre novamente.')
                    || 'Falha ao carregar prompts'
                toastr.error('Erro', msg)
                dispatch({
                    type: 'CRASH_PROMPT_FETCHED',
                    payload: { data: { data: [] } }
                })
            })
    }
}

export function create(values) {
    return submit(values, 'post')
}

export function update(values) {
    Object.keys(values).forEach(function (key) {
        if (values[key] && values[key].id) delete values[key]
    })
    return submit(values, 'put')
}

export function remove(values) {
    return submit(values, 'delete')
}

export function toggleActive(prompt) {
    return dispatch => {
        const next = Number(prompt.active) === 1 ? 0 : 1
        axios.put(`${process.env.REACT_APP_API_HOST}/crash_prompts/${prompt.id}`, {
            title: prompt.title,
            min_result: normalizeMinResult(prompt.min_result),
            history_days: normalizeHistoryDays(prompt.history_days),
            system_prompt: prompt.system_prompt,
            instruction: prompt.instruction,
            schedules: prompt.schedules,
            active: next,
        })
            .then(() => {
                dispatch(getList())
            })
            .catch(e => {
                toastr.error('Erro', (e.response && e.response.data && e.response.data.message) || 'Falha ao atualizar prompt')
            })
    }
}

function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Resultado mínimo (>=) válido; 2x quando ausente/ inválido. */
export function normalizeMinResult(value) {
    const n = Number(value)
    return Number.isFinite(n) && n > 1 ? n : 2
}

/** Intervalo de dados em dias (1–90; padrão 7). */
export function normalizeHistoryDays(value) {
    const n = Math.floor(Number(value))
    if (!Number.isFinite(n) || n < 1) return 7
    if (n > 90) return 90
    return n
}

/** Título base sem sufixo "cópia N" + próximo N entre os títulos da lista. */
export function nextCopyTitle(sourceTitle, list) {
    const base = String(sourceTitle || '').replace(/\s+cópia\s+\d+$/i, '').trim() || 'Prompt'
    const re = new RegExp(`^${escapeRegExp(base)}(?:\\s+cópia\\s+(\\d+))?$`, 'i')
    let max = 0
    ;(list || []).forEach((p) => {
        const m = String(p.title || '').match(re)
        if (m && m[1]) {
            const n = Number.parseInt(m[1], 10)
            if (Number.isFinite(n) && n > max) max = n
        }
    })
    return `${base} cópia ${max + 1}`
}

/** Cria cópia inativa do prompt com título "… cópia N". */
export function duplicate(prompt) {
    return (dispatch, getState) => {
        if (!prompt) return
        const list = (getState().crashPrompt && getState().crashPrompt.list) || []
        const payload = {
            title: nextCopyTitle(prompt.title, list),
            min_result: normalizeMinResult(prompt.min_result),
            history_days: normalizeHistoryDays(prompt.history_days),
            system_prompt: prompt.system_prompt,
            instruction: prompt.instruction,
            schedules: prompt.schedules,
            active: 0,
        }
        axios.post(`${process.env.REACT_APP_API_HOST}/crash_prompts`, payload)
            .then(() => {
                toastr.success('Sucesso', `Prompt duplicado: ${payload.title}`)
                dispatch(getList())
            })
            .catch(e => {
                toastr.error('Erro', (e.response && e.response.data && e.response.data.message) || 'Falha ao duplicar prompt')
            })
    }
}

function submit(values, method) {
    return dispatch => {
        const id = values.id ? values.id + 0 : ''

        // Só colunas reais: atributos calculados na listagem (accuracy,
        // accuracy_wins, accuracy_decided, win_streak) fazem a ACL recusar o
        // POST/PUT com 401.
        const filteredValues = {
            title: values.title,
            min_result: normalizeMinResult(values.min_result),
            history_days: normalizeHistoryDays(values.history_days),
            system_prompt: values.system_prompt,
            instruction: values.instruction,
            schedules: values.schedules_text != null
                ? textToSchedules(values.schedules_text)
                : values.schedules,
            active: Number(values.active) ? 1 : 0,
        }

        dispatch(getValidation({}))
        axios[method](`${process.env.REACT_APP_API_HOST}/crash_prompts${id ? '/' + id : ''}`, filteredValues)
            .then(() => {
                dispatch(init())
                dispatch(getList())
            })
            .catch(e => {
                if (!e.response) {
                    toastr.error('Erro', 'Desconhecido :-/')
                } else if (e.response.data && e.response.data.errors) {
                    dispatch(getValidation(e.response.data.errors))
                } else if (e.response.data) {
                    toastr.error('Erro', e.response.data.message)
                }
            })
    }
}

export function showContent(flag) {
    return {
        type: 'CRASH_PROMPT_CONTENT_CHANGED',
        payload: flag
    }
}

export function showUpdate(prompt) {
    return [
        showContent('form'),
        initialize('crashPromptForm', {
            ...prompt,
            min_result: normalizeMinResult(prompt.min_result),
            history_days: normalizeHistoryDays(prompt.history_days),
            schedules_text: schedulesToText(prompt.schedules),
            active: Number(prompt.active) === 1 ? 1 : 0,
        })
    ]
}

export function showCreate() {
    return [
        showContent('form'),
        initialize('crashPromptForm', INITIAL_VALUES)
    ]
}

export function init() {
    return [
        initialize('crashPromptForm', INITIAL_VALUES),
        showContent('list')
    ]
}

export function getValidation(errors) {
    return {
        type: 'CRASH_PROMPT_FORM_ERRORS',
        payload: errors
    }
}

export const crashPromptReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case 'CRASH_PROMPT_FETCHED':
            return { ...state, list: action.payload.data ? action.payload.data.data : [] }
        case 'CRASH_PROMPT_CONTENT_CHANGED':
            return { ...state, show: action.payload }
        case 'CRASH_PROMPT_FORM_ERRORS':
            return { ...state, errors: action.payload }
        default:
            return state
    }
}
